import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Issue, IssueSeverity, Handover } from '@/types/common';
import { Button, SearchableDropdown, SideBarModal } from '@/components';
import { StepIndicator } from '@/components/StepIndicator';
import { IssueFormData } from './ReportIssueModal';
import { getListHandover } from '@/services/api/handovers';
import { updateIssue, UpdateIssueRequest } from '@/services/api/issues';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { v4 as uuidv4 } from 'uuid';
import {
  getBucketUrlImageV2,
  uploadFileImage,
  getUserEmail,
  extractApiErrorMessage,
} from '@/utils';
import dayjs from 'dayjs';

interface EditIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  issue: Issue | null;
  onSuccess?: () => void;
}

// utility constants
const issueTypeOptions = [
  {
    value: 'Kerusakan akibat Kecelakaan',
    label: 'Kerusakan akibat Kecelakaan',
  },
  {
    value: 'Kerusakan akibat Penggunaan',
    label: 'Kerusakan akibat Penggunaan',
  },
  //   { value: 'Kehilangan Unit', label: 'Kehilangan Unit' },
];

const severityOptions = [
  { value: IssueSeverity.LOW, label: 'Low' },
  { value: IssueSeverity.MEDIUM, label: 'Medium' },
  { value: IssueSeverity.HIGH, label: 'High' },
  { value: IssueSeverity.CRITICAL, label: 'Critical' },
];

const costBearerOptions = [
  { value: 'MITRA', label: 'Mitra' },
  { value: 'CLIENT', label: 'Client' },
  { value: 'OEM', label: 'OEM' },
  { value: 'DASH_ELECTRIC', label: 'Dash' },
];

// Utility component for field display
const FieldDisplay: React.FC<{
  label: string;
  value: string | React.ReactNode;
  className?: string;
  hasBorder?: boolean;
  labelClassName?: string;
}> = ({
  label,
  value,
  className = '',
  hasBorder = true,
  labelClassName = '',
}) => (
  <div className={`pb-2 ${className}`}>
    <label className={`block mb-1 ${labelClassName || 'heading-field'}`}>
      {label}
    </label>
    <div
      className={`text-sm font-medium text-gray-900 break-words pb-3 ${hasBorder ? 'border-b border-gray-200' : ''}`}
    >
      {value || '-'}
    </div>
  </div>
);

// Section Header
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-gray-50 px-6 py-1 mb-4">
    <span className="font-medium rounded-t-lg heading-field tracking-wider">
      {title}
    </span>
  </div>
);

// Reusable Info Section
const InfoSection: React.FC<{ issue: Issue }> = ({ issue }) => (
  <>
    <SectionHeader title="Info" />
    <div className="p-6 pt-0">
      <div className="grid grid-cols-2 gap-4">
        <FieldDisplay
          label="Created By"
          value={issue.createdBy || '-'}
          className="mb-0"
          hasBorder={false}
        />
        <FieldDisplay
          label="Created Date"
          value={dayjs(issue.createdAt).format('MMMM D, YYYY [at] HH.mm')}
          className="mb-0"
          hasBorder={false}
        />
      </div>
      <div className="border-t border-gray-200 mt-2 mb-4" />
      <div className="grid grid-cols-2 gap-4">
        <FieldDisplay
          label="Last Updated By"
          value={issue.updatedBy}
          hasBorder={false}
        />
        <FieldDisplay
          label="Last Updated"
          value={
            issue.updatedAt
              ? dayjs(issue.updatedAt).format('MMMM D, YYYY [at] HH.mm')
              : '-'
          }
          hasBorder={false}
        />
      </div>
    </div>
  </>
);

export const EditIssueModal: React.FC<EditIssueModalProps> = ({
  isOpen,
  onClose,
  issue,
  onSuccess,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'details'>('general');
  const [isEditing, setIsEditing] = useState(false);

  // IDR display state for dot separator
  const [idrDisplayCost, setIdrDisplayCost] = useState('');

  const handleCostChange = (raw: string) => {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    const numValue = parseInt(digitsOnly, 10) || 0;
    const formatted = numValue === 0 ? '' : numValue.toLocaleString('id-ID');
    setIdrDisplayCost(formatted);
    setFormData((prev) => ({ ...prev, estimatedCost: numValue }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 KB';
    return Math.ceil(bytes / 1024) + ' KB';
  };

  const removeFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const removeExistingImage = () => {
    setFormData((prev) => ({ ...prev, imageURL: '' }));
  };

  // Edit Form State
  const [currentStep, setCurrentStep] = useState(1);
  const [handovers, setHandovers] = useState<Handover[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [formData, setFormData] = useState<IssueFormData>({
    handoverID: '',
    vehiclePlatNomor: '',
    issueType: '',
    incidentDate: '',
    severity: IssueSeverity.LOW,
    estimatedCost: 0,
    costBearer: '',
    mitraName: '',
    clientName: '',
    vehicleOemName: '',
    photos: [],
    notes: '',
  });

  // Reset state when opening/closing
  useEffect(() => {
    if (isOpen && issue) {
      setIsEditing(false);
      setActiveTab('general');
      setCurrentStep(1);
    }
  }, [isOpen, issue]);

  // Load handovers when entering edit mode
  const loadHandovers = useCallback(async () => {
    setIsLoadingData(true);
    try {
      let response;
      if (issue && issue.vehiclePlatNomor) {
        response = await getListHandover({
          page: '1',
          size: '50',
          search: issue.vehiclePlatNomor,
        });
      } else {
        response = await getListHandover({
          page: '1',
          size: '999',
          statuses: 'ACTIVE',
        });
      }
      setHandovers(response.data || []);

      // Prefill formData logic related to handovers
      if (issue) {
        const matched = (response.data || []).find(
          (h) => h.platNomor === issue.vehiclePlatNomor
        );
        if (matched) {
          setFormData((prev) => ({
            ...prev,
            handoverID: matched.ID,
            mitraName: `${matched.driverCode || ''} - ${matched.driverName || 'No Name'}`,
            clientName: matched.clientName || matched.client || '',
            vehicleOemName: matched.oemName || issue.vehicleOemName || '',
            oemID: matched.oemID || issue.oemID || '',
            driverID: matched.driverID || issue.driverID || undefined,
            driverCode: matched.driverCode || issue.driverCode || undefined,
            driverName: matched.driverName || issue.driverName || undefined,
            clientID: matched.clientID
              ? Number(matched.clientID)
              : issue.clientID
                ? Number(issue.clientID)
                : undefined,
          }));
        } else {
          // Fallback if vehicle not found in list (e.g. not ACTIVE)
          setFormData((prev) => ({
            ...prev,
            handoverID: 'CURRENT',
            mitraName: `${issue.mitraCode || ''} - ${issue.mitraName || ''}`,
            clientName: issue.clientName || '',
            vehicleOemName: issue.vehicleOemName || '',
            oemID: issue.oemID || '',
            clientID: issue.clientID ? Number(issue.clientID) : undefined,
            driverID: issue.driverID,
            driverCode: issue.driverCode,
            driverName: issue.driverName,
          }));
        }
      }
    } catch (error) {
      console.error('Error loading handovers:', error);
    } finally {
      setIsLoadingData(false);
    }
  }, [issue]);

  const ISSUE_TYPE_MAP: Record<string, string> = {
    ACCIDENT_DAMAGE: 'Kerusakan akibat Kecelakaan',
    USAGE_DAMAGE: 'Kerusakan akibat Penggunaan',
    LOST_UNIT: 'Kehilangan Unit',
  };

  // Helper function to normalize severity text
  const normalizeSeverity = (severity: string) => {
    if (!severity) return '-';
    return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
  };

  const handleEditClick = () => {
    setIsEditing(true);
    // If user is on "Details" view tab, go to Step 2 (Details/Photos) directly
    if (activeTab === 'details') {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 0);
    if (issue) {
      setFormData({
        handoverID: '',
        vehiclePlatNomor: issue.vehiclePlatNomor,
        issueType: ISSUE_TYPE_MAP[issue.issueType] || issue.issueType,
        incidentDate: issue.incidentDate
          ? dayjs(issue.incidentDate).format('YYYY-MM-DD')
          : '',
        severity: issue.severity,
        estimatedCost: issue.estimatedCost || 0,
        costBearer: issue.costBearer || (issue as any).costResponsibility || '',
        mitraName: issue.mitraName || '',
        clientName: issue.clientName || '',
        driverCode: issue.driverCode || undefined,
        driverName: issue.driverName || undefined,
        vehicleOemName: issue.vehicleOemName || '',
        oemID: issue.oemID || '',
        clientID: issue.clientID,
        driverID: issue.driverID,
        photos: [],
        imageURL: issue.imageURL || '',
        notes: issue.notes || '',
      });
      // Init formatted cost display
      const cost = issue.estimatedCost || 0;
      setIdrDisplayCost(cost === 0 ? '' : cost.toLocaleString('id-ID'));
      loadHandovers();
    }
  };

  const handoverOptions = useMemo(() => {
    const options = handovers.map((h) => ({
      value: h.ID,
      label: `${h.platNomor} - ${h.oemName} - ${h.modelName}`,
    }));

    // Inject current issue vehicle if not found in list
    if (issue && isEditing && formData.handoverID === 'CURRENT') {
      options.unshift({
        value: 'CURRENT',
        label: `${issue.vehiclePlatNomor} - ${issue.vehicleModelName || 'Unknown Model'}`,
      });
    }
    return options;
  }, [handovers, issue, isEditing, formData.handoverID]);

  const selectedHandover = useMemo(() => {
    if (formData.handoverID === 'CURRENT' && issue) {
      // Construct a mock handover object from issue data
      return {
        driverName: issue.driverName,
        driverCode: issue.driverCode,
        mitraName: issue.mitraName,
        clientName: issue.clientName,
        clientID: issue.clientID,
        oemName: issue.vehicleOemName,
        oemID: issue.oemID,
      };
    }
    return handovers.find((h) => h.ID === formData.handoverID);
  }, [formData.handoverID, handovers, issue]);

  const availableCostBearerOptions = useMemo(() => {
    if (!selectedHandover) return costBearerOptions;

    return costBearerOptions.filter((option) => {
      if (option.value === 'MITRA') {
        return !!(
          selectedHandover.driverName ||
          (selectedHandover as any).mitraName ||
          selectedHandover.driverCode
        );
      }
      if (option.value === 'CLIENT') {
        return !!(selectedHandover.clientName || selectedHandover.clientID);
      }
      if (option.value === 'OEM') {
        return !!(selectedHandover.oemName || selectedHandover.oemID);
      }
      return true; // Dash is always available
    });
  }, [selectedHandover]);

  useEffect(() => {
    if (
      formData.costBearer &&
      !availableCostBearerOptions.some(
        (opt) => opt.value === formData.costBearer
      )
    ) {
      setFormData((prev) => ({ ...prev, costBearer: '' }));
    }
  }, [availableCostBearerOptions, formData.costBearer]);

  const handleSave = async () => {
    if (!issue) return;
    setIsLoadingData(true);
    try {
      let imageURL = formData.imageURL || '';

      // Handle Image Upload if new file is selected
      if (formData.photos && formData.photos.length > 0) {
        const file = formData.photos[0];
        const uuid = uuidv4();

        const bucketRes = await getBucketUrlImageV2({
          uuid,
          type: 'issue',
        });
        const url = bucketRes.data;

        // 2. Convert to Base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
        });
        const base64Data = base64.split(',')[1];

        // 3. Upload File
        const uploadRes = await uploadFileImage(url, base64Data, file.name);

        if (uploadRes.status === 200) {
          imageURL = url;
        } else {
          throw new Error('Image upload failed');
        }
      }

      const userEmail = getUserEmail();
      if (!userEmail) throw new Error('User email not found');

      const payload: UpdateIssueRequest = {
        costResponsibility: formData.costBearer,
        severity: formData.severity,
        estimatedCost: formData.estimatedCost,
        notes: formData.notes,
        imageURL: imageURL,
        updatedBy: userEmail,
        oemID: formData.costBearer === 'OEM' ? formData.oemID : undefined,
        driverID: formData.driverID ? Number(formData.driverID) : undefined,
        driverCode: formData.driverCode,
        driverName: formData.driverName,
        clientID:
          formData.costBearer === 'CLIENT' ? formData.clientID : undefined,
        clientName: formData.clientName,
        incidentDate: formData.incidentDate,
      };

      await updateIssue(issue.ID, payload);

      showSuccessToast('Success', 'Issue updated successfully');
      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const msg = extractApiErrorMessage(error, 'Failed to update issue');
      showErrorToast('Error', msg);
    } finally {
      setIsLoadingData(false);
    }
  };

  if (!isOpen || !issue) return null;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={onClose}
        position="right"
        width="sm"
        contentRef={scrollRef}
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Edit Issues Ticket
            </h2>
            <p className="text-sm text-gray-600">Update Issues details.</p>

            {isEditing ? (
              <div className="flex justify-center pb-2 mt-3">
                <StepIndicator
                  steps={[
                    { id: 1, label: 'General Data' },
                    { id: 2, label: 'Details' },
                  ]}
                  currentStep={currentStep}
                />
              </div>
            ) : (
              <div className="flex space-x-6">
                <button
                  onClick={() => {
                    setActiveTab('general');
                    setTimeout(() => {
                      if (scrollRef.current) scrollRef.current.scrollTop = 0;
                    }, 0);
                  }}
                  className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'general'
                      ? 'border-purple-600 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  General Data
                </button>
                <button
                  onClick={() => {
                    setActiveTab('details');
                    setTimeout(() => {
                      if (scrollRef.current) scrollRef.current.scrollTop = 0;
                    }, 0);
                  }}
                  className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'details'
                      ? 'border-purple-600 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Details
                </button>
              </div>
            )}
          </div>
        }
        footer={
          isEditing ? (
            <>
              {currentStep === 1 ? (
                <Button
                  variant="primary"
                  className="w-full py-3"
                  onClick={() => {
                    setCurrentStep(2);
                    setTimeout(() => {
                      if (scrollRef.current) {
                        scrollRef.current.scrollTop = 0;
                      }
                    }, 0);
                  }}
                  disabled={
                    !formData.issueType ||
                    !formData.incidentDate ||
                    !formData.severity ||
                    !formData.costBearer
                  }
                >
                  Next
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCurrentStep(1);
                      setTimeout(() => {
                        if (scrollRef.current) {
                          scrollRef.current.scrollTop = 0;
                        }
                      }, 0);
                    }}
                    className="flex-1 py-3"
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    className="flex-1 py-3"
                    disabled={
                      isLoadingData ||
                      !formData.issueType ||
                      !formData.incidentDate ||
                      !formData.severity ||
                      !formData.costBearer
                    }
                  >
                    {isLoadingData ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <button
              className="w-full py-3 rounded-md font-medium text-sm transition-colors"
              onClick={handleEditClick}
              style={{
                backgroundColor: '#784DEF1A',
                color: '#5E2AAC',
                border: 'none',
              }}
            >
              Edit
            </button>
          )
        }
      >
        <div className="flex-1">
          {!isEditing ? (
            // READ ONLY MODE
            <>
              {activeTab === 'general' && (
                <div>
                  <div className="p-6 pb-0">
                    <FieldDisplay
                      label="Plate Number"
                      value={issue.vehiclePlatNomor}
                      className="mb-2"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FieldDisplay
                        label="OEM Name"
                        value={issue.vehicleOemName || '-'}
                        hasBorder={false}
                      />
                      <FieldDisplay
                        label="Model Name"
                        value={issue.vehicleModelName || '-'}
                        hasBorder={false}
                      />
                    </div>
                  </div>

                  <SectionHeader title="Issue Detail" />

                  <div className="p-6 pt-0 pb-0">
                    <FieldDisplay
                      label="Issue Type"
                      value={ISSUE_TYPE_MAP[issue.issueType] || issue.issueType}
                      className="mb-2"
                    />
                    <FieldDisplay
                      label="Tanggal Kejadian"
                      value={
                        issue.incidentDate
                          ? dayjs(issue.incidentDate).format('DD/MM/YYYY')
                          : '-'
                      }
                      className="mb-2"
                    />
                    <FieldDisplay
                      label="Severity"
                      value={normalizeSeverity(issue.severity)}
                      className="mb-2"
                    />
                    <FieldDisplay
                      label="Estimated Cost"
                      value={
                        issue.estimatedCost !== undefined &&
                        issue.estimatedCost !== null
                          ? `Rp. ${issue.estimatedCost.toLocaleString('id-ID')}`
                          : '-'
                      }
                      className="mb-2"
                    />

                    {/* Resolve Cost Bearer value properly */}
                    {(() => {
                      const costBearer =
                        issue.costBearer || (issue as any).costResponsibility;
                      // Handle potential casing differences just in case (e.g. Mitra vs MITRA)
                      const costBearerUpper = costBearer?.toUpperCase();

                      // Map display label
                      const costBearerLabel =
                        costBearerOptions.find(
                          (opt) => opt.value === costBearerUpper
                        )?.label || costBearer;

                      return (
                        <>
                          <FieldDisplay
                            label="Biaya Ditanggung"
                            value={costBearerLabel}
                            className={
                              costBearerUpper === 'DASH_ELECTRIC'
                                ? 'mb-0'
                                : 'mb-2'
                            }
                            hasBorder={costBearerUpper !== 'DASH_ELECTRIC'}
                          />

                          {costBearerUpper === 'MITRA' && (
                            <FieldDisplay
                              label="Mitra Name"
                              value={issue.mitraName || issue.driverName || '-'}
                              hasBorder={false}
                            />
                          )}
                          {costBearerUpper === 'CLIENT' && (
                            <FieldDisplay
                              label="Client Name"
                              value={issue.clientName}
                              hasBorder={false}
                            />
                          )}
                          {costBearerUpper === 'OEM' && (
                            <FieldDisplay
                              label="OEM Name"
                              value={issue.vehicleOemName}
                              hasBorder={false}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <InfoSection issue={issue} />
                </div>
              )}
              {activeTab === 'details' && (
                <div>
                  <div className="p-6 pb-0">
                    <FieldDisplay
                      label="Foto Kendaraan"
                      value={
                        issue.imageURL ? (
                          <div className="rounded-lg overflow-hidden mt-2 mb-2 border border-gray-100">
                            <img
                              src={issue.imageURL}
                              alt="Vehicle Evidence"
                              className="w-full h-40 object-cover bg-gray-100"
                            />
                          </div>
                        ) : (
                          <div className="border-2 border-gray-200 rounded-lg mt-2 mb-2">
                            <div className="p-8 flex flex-col justify-center items-center text-center">
                              <div className="text-gray-300 text-4xl mb-4">
                                <svg
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  className="w-12 h-12 mx-auto"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                              <p className="text-gray-500 font-medium">
                                No issue image uploaded
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Image was not provided during issue creation
                              </p>
                            </div>
                          </div>
                        )
                      }
                      hasBorder={true}
                    />
                    <div className="pt-2">
                      <FieldDisplay
                        label="Notes"
                        value={issue.notes || '-'}
                        hasBorder={false}
                      />
                    </div>
                  </div>

                  <InfoSection issue={issue} />
                </div>
              )}
            </>
          ) : (
            // EDIT MODE
            <div className="p-6">
              {currentStep === 1 && (
                <div className="space-y-4">
                  {/* Plate Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plate Number <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={handoverOptions}
                      value={formData.handoverID}
                      onChange={(val) =>
                        setFormData((prev) => ({ ...prev, handoverID: val }))
                      }
                      placeholder={
                        isLoadingData
                          ? 'Loading...'
                          : 'e.g. B 1234 ABC - Electrum - H3'
                      }
                      disabled={true}
                      className="w-full"
                    />
                  </div>

                  {/* Issue Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Issue Type <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={issueTypeOptions}
                      value={formData.issueType}
                      onChange={(val) =>
                        setFormData((prev) => ({ ...prev, issueType: val }))
                      }
                      placeholder="e.g. Kerusakan Unit Akibat Kecelakaan"
                      className="w-full"
                    />
                  </div>

                  {/* Tanggal Kejadian */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tanggal Kejadian <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 text-gray-900"
                      value={formData.incidentDate}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          incidentDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  {/* Severity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Severity <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={severityOptions}
                      value={formData.severity}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          severity: val as IssueSeverity,
                        }))
                      }
                      placeholder="e.g. Medium"
                      className="w-full"
                    />
                  </div>

                  {/* Estimasi Biaya */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimasi Biaya <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">Rp</span>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="w-full border border-gray-300 rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                        placeholder="0"
                        value={idrDisplayCost}
                        onChange={(e) => handleCostChange(e.target.value)}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                        <img
                          src="https://flagcdn.com/w40/id.png"
                          alt="IDR"
                          className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                        />
                        <span className="text-gray-500 text-sm">IDR</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Isi 0 jika tidak ada biaya.
                    </p>
                  </div>

                  {/* Biaya Ditanggung */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Biaya Ditanggung <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={availableCostBearerOptions}
                      value={formData.costBearer}
                      onChange={(val) =>
                        setFormData((prev) => ({ ...prev, costBearer: val }))
                      }
                      placeholder="e.g. Mitra"
                      className="w-full"
                    />
                  </div>

                  {/* Dynamic Detail Fields based on Cost Bearer */}
                  {formData.costBearer === 'MITRA' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mitra Name <span className="text-red-500">*</span>
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600">
                        {formData.mitraName || '-'}
                      </div>
                    </div>
                  )}
                  {formData.costBearer === 'CLIENT' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Name <span className="text-red-500">*</span>
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600">
                        {formData.clientName || '-'}
                      </div>
                    </div>
                  )}
                  {formData.costBearer === 'OEM' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        OEM Name <span className="text-red-500">*</span>
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-600">
                        {formData.vehicleOemName || '-'}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  {/* Photo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto Kendaraan
                    </label>
                    {formData.photos.length > 0 ? (
                      <div className="space-y-3">
                        {formData.photos.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-xl bg-white"
                          >
                            <div className="flex items-center space-x-4">
                              {/* Document Icon */}
                              <div className="w-12 h-12 flex-shrink-0 relative">
                                <svg
                                  viewBox="0 0 40 40"
                                  fill="none"
                                  className="w-full h-full"
                                >
                                  <path
                                    d="M12 4H24L32 12V34C32 35.1046 31.1046 36 30 36H12C10.8954 36 10 35.1046 10 34V6C10 4.89543 10.8954 4 12 4Z"
                                    fill="white"
                                    stroke="#9CA3AF"
                                    strokeWidth="2"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M24 4V10C24 11.1046 24.8954 12 26 12H32"
                                    stroke="#9CA3AF"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <rect
                                    x="6"
                                    y="20"
                                    width="28"
                                    height="13"
                                    rx="4"
                                    fill="#6B7280"
                                  />
                                  <text
                                    x="20"
                                    y="29.5"
                                    textAnchor="middle"
                                    fill="white"
                                    fontSize="9"
                                    fontWeight="bold"
                                    fontFamily="sans-serif"
                                  >
                                    JPG
                                  </text>
                                </svg>
                              </div>
                              <div>
                                <p className="text-[14px] font-medium text-gray-900 truncate max-w-[200px]">
                                  {file.name}
                                </p>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                                  <span>{formatFileSize(file.size)}</span>
                                  <span>•</span>
                                  <span className="text-green-500 flex items-center gap-1 font-medium">
                                    <i className="ri-checkbox-circle-fill"></i>{' '}
                                    Completed
                                  </span>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => removeFile(index)}
                              className="text-gray-500 hover:text-red-500 transition-colors"
                            >
                              <i className="ri-delete-bin-line text-xl"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : formData.imageURL ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl bg-white">
                          <div className="flex items-center space-x-4">
                            {/* Document Icon */}
                            <div className="w-12 h-12 flex-shrink-0 relative">
                              <svg
                                viewBox="0 0 40 40"
                                fill="none"
                                className="w-full h-full"
                              >
                                <path
                                  d="M12 4H24L32 12V34C32 35.1046 31.1046 36 30 36H12C10.8954 36 10 35.1046 10 34V6C10 4.89543 10.8954 4 12 4Z"
                                  fill="white"
                                  stroke="#9CA3AF"
                                  strokeWidth="2"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M24 4V10C24 11.1046 24.8954 12 26 12H32"
                                  stroke="#9CA3AF"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <rect
                                  x="6"
                                  y="20"
                                  width="28"
                                  height="13"
                                  rx="4"
                                  fill="#6B7280"
                                />
                                <text
                                  x="20"
                                  y="29.5"
                                  textAnchor="middle"
                                  fill="white"
                                  fontSize="9"
                                  fontWeight="bold"
                                  fontFamily="sans-serif"
                                >
                                  JPG
                                </text>
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium text-[14px] text-gray-900 truncate max-w-[200px]">
                                Issue Image.PNG
                              </p>
                              <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                                <span className="text-green-500 flex items-center gap-1 font-medium">
                                  <i className="ri-checkbox-circle-fill"></i>{' '}
                                  Saved
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={removeExistingImage}
                            className="text-gray-500 hover:text-red-500 transition-colors"
                          >
                            <i className="ri-delete-bin-line text-xl"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors relative">
                        <input
                          type="file"
                          accept="image/*"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              setFormData((prev) => ({
                                ...prev,
                                photos: Array.from(e.target.files!),
                              }));
                            }
                          }}
                        />
                        <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                        <p className="text-sm text-gray-900 font-medium">
                          Choose a file or drag & drop it here.
                        </p>
                        <div className="mt-4">
                          <span className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-600 bg-white">
                            Browse File
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-24 focus:ring-purple-500 focus:border-purple-500"
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      placeholder="Add notes about the issue..."
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </SideBarModal>
    </>
  );
};
